import React, { useMemo } from 'react';
import { Typography } from '@mui/material';
import { isNumber, round } from 'lodash';
import type { SurfHeight } from 'types/units';
import conditionIsFlat from 'utils/conditionIsFlat';

import classNames from 'classnames';
import styles from './SurfCell.module.scss';

export const shadingMapper = (rawMax: number, units: string | undefined, dark: boolean) => {
  const roundingValue = units?.toUpperCase() === 'M' ? 1 : 0;
  let convertedHeight = round(rawMax, roundingValue);

  if (units?.toUpperCase() === 'M') {
    convertedHeight = round(rawMax * 3.28084, roundingValue);
  }

  if (units?.toUpperCase() === 'HI') {
    convertedHeight = round(rawMax * 2, roundingValue);
  }

  if (convertedHeight > 20) {
    // We start at 60 percent opacity for a max of 20 and go up to 50
    const opacity = ((convertedHeight - 20) * 1.333) / 100 + 0.6;
    return `rgba(107, 162, 187, ${round(dark ? opacity * 0.5 : opacity, 3)})`;
  }
  // We start at 5 percent opacity for a max of 0 and go up to but not including 20
  const opacity = (convertedHeight * 5) / 100 + 0.05;
  return `rgba(185, 211, 223, ${round(dark ? opacity * 0.5 : opacity, 3)})`;
};

type Props = {
  dark: boolean;
  loading: boolean;
  isDataObserved?: boolean;
  isMobile: boolean;
  max: number | null;
  min: number | null;
  rawMax: number | null;
  plus: boolean;
  units: SurfHeight | string | undefined;
};

const SurfCell: React.FC<Props> = ({
  isDataObserved = false,
  max,
  min,
  rawMax,
  isMobile,
  loading,
  plus,
  units,
  dark = false,
}) => {
  const error = !isNumber(max) || !isNumber(min) || !isNumber(rawMax);
  const flat = conditionIsFlat(max || 0, units as unknown as string);
  const loadingOrError = error || loading;
  const backgroundShading = shadingMapper(rawMax || 0, units, dark);

  const style = useMemo(
    () => ({
      background: isDataObserved ? 'none' : backgroundShading,
    }),
    [isDataObserved, backgroundShading],
  );

  return (
    <div
      data-testid="forecast-table-surf-cell"
      className={classNames({
        [styles.wrapper]: true,
        [styles.observed]: isDataObserved,
      })}
      style={style}
    >
      {loadingOrError ? (
        <Typography
          className={styles.height}
          color="text.light"
          data-testid="forecast-table-surf-empty"
          variant={isMobile ? 'subHeadline' : 'headline'}
        >
          --
        </Typography>
      ) : (
        <Typography
          className={styles.height}
          color={flat ? 'text.secondary' : 'text.primary'}
          data-testid="forecast-table-surf-height"
          variant={isMobile ? 'subHeadline' : 'headline'}
        >
          {flat ? 'Flat' : `${min}-${max}${plus ? '+' : ''}`}
        </Typography>
      )}
    </div>
  );
};

export default SurfCell;
